<template>
  <section
    class="big-card-carousel"
    :data-ab-id="dataAbId"
  >
    <h2
      v-if="topTitle"
      class="flex items-end gap-1"
    >
      {{ topTitle }}
      <KikLegalInformationDrawer
        v-if="enableLegalDisclaimer && titleOrSubtitle === 'title'"
        :legal-text="legalText"
        :title="legalTitle"
      />
    </h2>
    <p class="flex items-end gap-1">
      {{ subtitle }}
      <KikLegalInformationDrawer
        v-if="enableLegalDisclaimer && titleOrSubtitle === 'subtitle'"
        :legal-text="legalText"
        :title="legalTitle"
      />
    </p>
    <KikCarousel
      v-if="products.length > 0"
    >
      <div
        v-for="(product, index) in products"
        :key="index"
        class="w-[70vw] shrink-0 rounded-md first:ms-auto last:me-auto hover:shadow-lg md:w-[250px] lg:w-[350px] xl:w-[400px]"
      >
        <SharedTeaserCard
          :title="product.title"
          :title-color="product.titleColor"
          :src="product.src"
          :src-set="product.srcset"
          :alt="product.alt"
          :introduction="product.subtitle"
          :introduction-color="product.subtitleColor"
          :link="product.mode === 'product' ? product.productLink : product.ctaLink"
          :is-my-kik-discount="product.isMyKikDiscount"
        />
      </div>
    </KikCarousel>
  </section>
</template>

<script lang="ts" setup>
import type { ctaLinkStruct } from './BigCardCarousel.model';
import { useAppLocale } from '~/composables/custom/useAppLocale';
import magnoliaGetters from '~/composables/getters/magnoliaGetters';
import { useProduct } from '~/composables/custom/useProduct/useProduct';
import productGetters from '~/composables/getters/productGetters';
import type Asset from '~/magnolia/lib/MagnoliaAsset';

interface Item {
  titleColor: string;
  subtitleColor: string;
  productId: string;
  field?: string | undefined;
  title?: string;
  subtitle?: string;
  image?: Asset;
  aspectRatio?: string;
  mobileAspectRatio?: string;
  ctaLink?: Array<ctaLinkStruct>;
  product?: string | undefined;
  linkTargetMode?: string;
}

interface Props {
  topTitle?: string;
  topTitleColor?: string;
  subtitle?: string;
  subtitleColor?: string;
  items?: Item[];
  productsIds?: string[];
  dataAbId?: string;
  enableLegalDisclaimer?: boolean;
  legalText?: string;
  legalTitle?: string;
  titleOrSubtitle?: string;
}

const props = withDefaults(defineProps<Props>(), {
  topTitle: '',
  topTitleColor: '',
  subtitle: '',
  subtitleColor: '',
  enableLegalDisclaimer: false,
  legalText: '',
  legalTitle: '',
  titleOrSubtitle: 'title',
  aspectRatio: 'original',
  items: () => [],
  productsIds: () => [],
});

const { $domain } = useNuxtApp();
const { locale } = useAppLocale();
const { items, productsIds } = toRefs(props);
const { getProductsReduced } = useProduct();

const products = ref<any>([]);

onBeforeMount(async () => {
  const productsReduced = await getProductsReduced({
    products: productsIds.value,
  });

  products.value = items.value?.map((item) => {
    const productToDisplay = item?.field === 'product' ? productsReduced?.find(p => p.id === item.productId) : undefined;
    return {
      ...item,
      mode: item.field,
      src: item?.field === 'content' ? magnoliaGetters.getDefaultSrcAsset(item.image, $domain.cmsAssetDomainPath) : productToDisplay?.images[0]?.url,
      srcset: item?.field === 'content' ? magnoliaGetters.getSrcSetImagesAsString(item.image, $domain.cmsAssetDomainPath, item.aspectRatio as string, item.mobileAspectRatio?.[0]?.aspectRatio) : [],
      alt: item.image?.metadata?.caption ?? '',
      ctaLink: item.ctaLink?.[0] ? magnoliaGetters.getLinkAndName(item.ctaLink[0], $domain.cmsAssetDomainPath, item.ctaLink[0].switchable?.intLinkParam) : {},
      productLink: productToDisplay ? { url: productGetters.getProductUrl({ key: productToDisplay?.key, hrefLang: productToDisplay?.hrefLang }, locale), target: item?.linkTargetMode ?? '_self', type: 'page' } : '',
      isMyKikDiscount: productToDisplay ? productToDisplay?.isMyKikDiscount : false,
    };
  });
});
</script>

<style lang="scss" scoped>
.big-card-carousel {
  padding: 0 var(--spacer-base);
  h2 {
    color: v-bind(topTitleColor);
    font-size: var(--h2-font-size);
    font-weight: var(--font-weight--bold);
  }

  p {
    color: v-bind(subtitleColor);
    font-size: var(--font-size--base);
    margin: 0;
  }
}
</style>
